/**
 * 30-10-2026 (Jordy, Chats compacter): de uitleg van een box staat niet meer als
 * tekst onder de titel, maar achter een i-knopje naast de titel.
 *
 * - Desktop: tonen bij hover met de muis.
 * - Telefoon: tikken zet de uitleg open of dicht (tikken ernaast sluit hem).
 * - Toetsenbord: Tab naar het knopje toont hem, Enter/spatie zet hem vast,
 *   Escape sluit. Het knopje heeft een aria-label; de tekst hangt er via
 *   aria-describedby aan zolang hij zichtbaar is.
 * - De tekst staat `position: fixed` onder het knopje, zodat een scrollende
 *   lijst (Live Feed) hem niet afknipt. De pagina-zoom (Size, `--dz` op <html>)
 *   wordt teruggerekend; op een telefoon over de volle breedte met 16px marge.
 */
import { useEffect, useId, useLayoutEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react'

function plek(knop: HTMLElement | null): CSSProperties {
  if (!knop) return {}
  const dz = Number(getComputedStyle(document.documentElement).getPropertyValue('--dz')) || 1
  const r = knop.getBoundingClientRect()
  const top = (r.bottom + 6) / dz
  if (window.innerWidth < 900) return { top, left: 16, right: 16 }
  const breed = Math.min(340, window.innerWidth / dz - 32)
  const left = Math.max(16, Math.min(r.left / dz - 8, window.innerWidth / dz - breed - 16))
  return { top, left, maxWidth: breed }
}

export function InfoKnop({ label, children }: { label: string; children: ReactNode }) {
  const [vast, setVast] = useState(false)      // getikt/geklikt
  const [muis, setMuis] = useState(false)      // muis erboven (alleen een echte muis)
  const [focus, setFocus] = useState(false)    // toetsenbordfocus
  const id = useId()
  const ref = useRef<HTMLSpanElement>(null)
  const knop = useRef<HTMLButtonElement>(null)
  const open = vast || muis || focus
  const [stijl, setStijl] = useState<CSSProperties>({})

  useLayoutEffect(() => {
    if (!open) return
    const zet = () => setStijl(plek(knop.current))
    zet()
    window.addEventListener('scroll', zet, true)
    window.addEventListener('resize', zet)
    return () => {
      window.removeEventListener('scroll', zet, true)
      window.removeEventListener('resize', zet)
    }
  }, [open])

  useEffect(() => {
    if (!vast) return
    const buiten = (e: Event) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setVast(false)
    }
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') setVast(false) }
    document.addEventListener('pointerdown', buiten)
    document.addEventListener('keydown', esc)
    return () => {
      document.removeEventListener('pointerdown', buiten)
      document.removeEventListener('keydown', esc)
    }
  }, [vast])

  return (
    <span
      className="infoknop"
      ref={ref}
      onPointerEnter={(e) => { if (e.pointerType === 'mouse') setMuis(true) }}
      onPointerLeave={(e) => { if (e.pointerType === 'mouse') setMuis(false) }}
    >
      <button
        type="button"
        className="infoknop-i"
        ref={knop}
        data-testid="infoknop"
        aria-label={label}
        aria-expanded={open}
        aria-describedby={open ? id : undefined}
        // In een rij die een link is: niet doorklikken naar de chat.
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); setVast((v) => !v) }}
        onFocus={(e) => { if (e.currentTarget.matches(':focus-visible')) setFocus(true) }}
        onBlur={() => setFocus(false)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') { setVast(false); setFocus(false); setMuis(false) }
        }}
      >
        i
      </button>
      {open ? (
        <span role="tooltip" id={id} className="infoknop-tekst" data-testid="infoknop-tekst" style={stijl}>
          {children}
        </span>
      ) : null}
    </span>
  )
}
